// src/components/RacketDataComponent.js
import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import { useRacketDataContext } from "../context"
import FilterIcon from "../components/icon/filter"

const RacketDataComponent = ({
  stringMain,
  stringCross,
  brand,
  series,
  size,
  balance,
  year,
  weight,
  beam,
  pMinBeam,
  pMaxBeam,
}) => {
  const afid = process.env.GATSBY_MERCARI_AFID
  const data = useRacketDataContext()
  const rackets = data.allMdx.nodes

  const getUniqueSortedValues = key =>
    [
      ...new Set(
        rackets.map(({ frontmatter }) => frontmatter[key]).filter(Boolean)
      ),
    ].sort((a, b) => a - b)

  // ユニークなブランド一覧を取得
  const brands = getUniqueSortedValues("brand")
  const weights = getUniqueSortedValues("weight")
  const sizes = getUniqueSortedValues("size")
  const balances = getUniqueSortedValues("balance")
  const lengths = getUniqueSortedValues("length")
  const years = getUniqueSortedValues("year")

  const beams = [
    ...new Set(
      rackets
        .flatMap(({ frontmatter }) => [
          frontmatter.beam,
          frontmatter.beam_top,
          frontmatter.beam_mid,
          frontmatter.beam_throat,
        ])
        .filter(Boolean) // `null` や `undefined` を除外
    ),
  ].sort((a, b) => a - b)

  const stringPatterns = Array.from(
    new Map(
      rackets
        .map(({ frontmatter }) =>
          frontmatter.string_main && frontmatter.string_cross
            ? [
                `${frontmatter.string_main}/${frontmatter.string_cross}`, // 一意なキー
                {
                  main: frontmatter.string_main,
                  cross: frontmatter.string_cross,
                },
              ]
            : null
        )
        .filter(Boolean)
    ).values()
  ).sort((a, b) => (a.main !== b.main ? a.main - b.main : a.cross - b.cross))

  const brandSeriesPatterns = Array.from(
    new Map(
      rackets
        .map(({ frontmatter }) =>
          frontmatter.brand && frontmatter.series
            ? [
                `${frontmatter.brand}/${frontmatter.series}`, // 一意なキー
                { brand: frontmatter.brand, series: frontmatter.series },
              ]
            : null
        )
        .filter(Boolean)
    ).values()
  ).sort((a, b) =>
    a.brand !== b.brand
      ? a.brand.localeCompare(b.brand)
      : a.series.localeCompare(b.series)
  )

  // フィルター用のstate
  const [selectedBrands, setSelectedBrands] = useState([])
  const [selectedSeries, setSelectedSeries] = useState([])
  const [sortConfig, setSortConfig] = useState({ key: "jp", direction: "desc" })
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [isCompetition, setIsCompetition] = useState(false)
  const [minWeight, setMinWeight] = useState(
    weights.length > 0 ? Math.min(...weights) : ""
  )
  const [maxWeight, setMaxWeight] = useState(
    weights.length > 0 ? Math.max(...weights) : ""
  )
  const [minSize, setMinSize] = useState(
    sizes.length > 0 ? Math.min(...sizes) : ""
  )
  const [maxSize, setMaxSize] = useState(
    sizes.length > 0 ? Math.max(...sizes) : ""
  )
  const [minBalance, setMinBalance] = useState(
    balances.length > 0 ? Math.min(...balances) : ""
  )
  const [maxBalance, setMaxBalance] = useState(
    balances.length > 0 ? Math.max(...balances) : ""
  )
  const [minBeam, setMinBeam] = useState(
    beams.length > 0 ? Math.min(...beams) : ""
  )
  const [maxBeam, setMaxBeam] = useState(
    beams.length > 0 ? Math.max(...beams) : ""
  )
  const [minLength, setMinLength] = useState(
    lengths.length > 0 ? Math.min(...lengths) : ""
  )
  const [maxLength, setMaxLength] = useState(
    lengths.length > 0 ? Math.max(...lengths) : ""
  )
  const [minYear, setMinYear] = useState(
    years.length > 0 ? Math.min(...years) : ""
  )
  const [maxYear, setMaxYear] = useState(
    years.length > 0 ? Math.max(...years) : ""
  )

  const [selectedStrings, setSelectedStrings] = useState([])

  useEffect(() => {
    if (stringMain && stringCross) {
      setSelectedStrings([{ main: stringMain, cross: stringCross }])
    }
    if (brand) {
      setSelectedBrands([brand])
    }
    if (series) {
      setSelectedSeries([series])
    }
    if (size) {
      setMaxSize(size)
      setMinSize(size)
    }
    if (balance) {
      setMaxBalance(balance)
      setMinBalance(balance)
    }
    if (year) {
      setMaxYear(year)
      setMinYear(year)
    }
    if (weight) {
      setMaxWeight(weight)
      setMinWeight(weight)
    }
    if (beam) {
      setMaxBeam(beam)
      setMinBeam(beam)
    }
    if (pMinBeam) {
      setMinBeam(pMinBeam)
    }
    if (pMaxBeam) {
      setMaxBeam(pMaxBeam)
    }
  }, [
    stringMain,
    stringCross,
    brand,
    size,
    balance,
    year,
    weight,
    beam,
    pMinBeam,
    pMaxBeam,
  ])

  const handleBrandChange = brand => {
    setSelectedBrands(
      prevSelected =>
        prevSelected.includes(brand)
          ? prevSelected.filter(b => b !== brand) // すでに選択されていたら削除
          : [...prevSelected, brand] // 未選択なら追加
    )
  }

  const handleStringPatternChange = (main, cross) => {
    setSelectedStrings(
      prev =>
        prev.some(pattern => pattern.main === main && pattern.cross === cross)
          ? prev // すでに存在する場合は変更しない
          : [...prev, { main, cross }] // 存在しない場合のみ追加
    )
    document.getElementById("stringPatternSelect").value = ""
  }

  const removeStringPattern = index => {
    setSelectedStrings(selectedStrings.filter((_, i) => i !== index))
  }

  const handleSeriesChange = e => {
    const series = e.target.value
    setSelectedSeries(prev =>
      prev.includes(series) ? prev : [...prev, series]
    )
    document.getElementById("seriesSelect").value = ""
  }

  const removeSeries = index => {
    setSelectedSeries(selectedSeries.filter((_, i) => i !== index))
  }

  // フィルタリング処理
  const filteredRackets = rackets.filter(({ frontmatter: racket }) => {
    const weight = Number(racket.weight)
    const size = Number(racket.size)
    const balance = Number(racket.balance)
    const length = Number(racket.length)
    const year = Number(racket.year)

    const getBeamAverage = racket =>
      racket.beam
        ? Number(racket.beam)
        : (Number(racket.beam_throat || 0) +
            Number(racket.beam_mid || 0) +
            Number(racket.beam_top || 0)) /
          3
    const beam = getBeamAverage(racket)

    const brandMatch =
      selectedBrands.length > 0 ? selectedBrands.includes(racket.brand) : true
    const seriesMatch =
      selectedSeries.length > 0 ? selectedSeries.includes(racket.series) : true

    const weightMatch =
      (minWeight ? weight >= Number(minWeight) : true) &&
      (maxWeight ? weight <= Number(maxWeight) : true)
    const sizeMatch =
      (minSize ? size >= Number(minSize) : true) &&
      (maxSize ? size <= Number(maxSize) : true)
    const balanceMatch =
      (minBalance ? balance >= Number(minBalance) : true) &&
      (maxBalance ? balance <= Number(maxBalance) : true)

    const beamMatch =
      (minBeam ? beam >= Number(minBeam) : true) &&
      (maxBeam ? beam <= Number(maxBeam) : true)

    const lengthMatch =
      (minLength ? length >= Number(minLength) : true) &&
      (maxLength ? length <= Number(maxLength) : true)

    const yearMatch =
      (minYear ? year >= Number(minYear) : true) &&
      (maxYear ? year <= Number(maxYear) : true)

    const competitionMatch = isCompetition ? racket.is_competition === 1 : true

    const stringMatch =
      selectedStrings.length > 0
        ? selectedStrings.some(
            pattern =>
              pattern.main == racket.string_main &&
              pattern.cross == racket.string_cross
          )
        : true

    return (
      brandMatch &&
      seriesMatch &&
      weightMatch &&
      sizeMatch &&
      balanceMatch &&
      beamMatch &&
      lengthMatch &&
      yearMatch &&
      competitionMatch &&
      stringMatch
    )
  })

  // ソート処理
  const sortedRackets = [...filteredRackets].sort((a, b) => {
    if (!sortConfig.key) return 0

    let aValue = a.frontmatter[sortConfig.key] || ""
    let bValue = b.frontmatter[sortConfig.key] || ""

    // beam列の特殊なソート処理
    if (sortConfig.key === "beam") {
      const getBeamAverage = racket =>
        racket.beam
          ? Number(racket.beam)
          : (Number(racket.beam_throat) +
              Number(racket.beam_mid) +
              Number(racket.beam_top)) /
            3

      aValue = getBeamAverage(a.frontmatter)
      bValue = getBeamAverage(b.frontmatter)
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue
    }

    return sortConfig.direction === "asc"
      ? String(aValue).localeCompare(String(bValue), "ja")
      : String(bValue).localeCompare(String(aValue), "ja")
  })

  const handleSort = (key, sortable) => {
    if (!sortable) return // ソート不可の列は処理しない
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }))
  }

  const openFilter = () => {
    setIsFilterVisible(true)
  }

  const closeFilter = () => {
    setIsFilterVisible(false)
  }

  const FilterComponent = () => {
    return (
      <div className={`filter-container`}>
        <div className="racket-num">{sortedRackets.length + `件ヒット`}</div>
        <div>
          <label>
            <input
              type="checkbox"
              checked={isCompetition}
              onChange={e => setIsCompetition(e.target.checked)}
            />
            &nbsp;競技者向け
          </label>
        </div>
        <div className="mt-2">
          ブランド
          <div className="filter-label-wrap">
            {brands.map(brand => (
              <label
                className={`filter-label ${
                  selectedBrands.includes(brand) ? "selected" : ""
                }`}
                key={brand}
              >
                <input
                  type="checkbox"
                  value={brand}
                  checked={selectedBrands.includes(brand)}
                  onChange={() => handleBrandChange(brand)}
                  style={{ display: "none" }}
                />
                {brand}
              </label>
            ))}
          </div>
        </div>
        <div className="mt-2">
          <label>
            シリーズ&nbsp;&nbsp;
            <select id="seriesSelect" onChange={handleSeriesChange}>
              <option value={null}></option>
              {brandSeriesPatterns
                .filter(pattern =>
                  selectedBrands.length > 0
                    ? selectedBrands.includes(pattern.brand)
                    : true
                )
                .map(pattern => (
                  <option value={pattern.series} key={pattern.series}>
                    {pattern.series}
                  </option>
                ))}
            </select>
          </label>
          {/* 選択されたストリングを表示 */}
          <div className="selected-strings">
            {selectedSeries.map((s, index) => (
              <span
                key={`selected${s}`}
                className="selected-string"
                onClick={() => removeSeries(index)}
              >
                {s} ✕
              </span>
            ))}
          </div>
        </div>
        {[
          {
            label: "発売年",
            min: minYear,
            max: maxYear,
            setMin: setMinYear,
            setMax: setMaxYear,
            values: years,
          },
          {
            label: "重量",
            min: minWeight,
            max: maxWeight,
            setMin: setMinWeight,
            setMax: setMaxWeight,
            values: weights,
          },
          {
            label: "サイズ",
            min: minSize,
            max: maxSize,
            setMin: setMinSize,
            setMax: setMaxSize,
            values: sizes,
          },
          {
            label: "バランス",
            min: minBalance,
            max: maxBalance,
            setMin: setMinBalance,
            setMax: setMaxBalance,
            values: balances,
          },
          {
            label: "フレーム",
            min: minBeam,
            max: maxBeam,
            setMin: setMinBeam,
            setMax: setMaxBeam,
            values: beams,
          },
          {
            label: "長さ",
            min: minLength,
            max: maxLength,
            setMin: setMinLength,
            setMax: setMaxLength,
            values: lengths,
          },
        ].map(({ label, min, max, setMin, setMax, values }) => (
          <div className="mt-2" key={label}>
            <label>
              {label}&nbsp;&nbsp;
              <select value={min} onChange={e => setMin(e.target.value)}>
                {values.map(v => (
                  <option value={v} key={v}>
                    {v}
                  </option>
                ))}
              </select>
              &nbsp;～&nbsp;
              <select value={max} onChange={e => setMax(e.target.value)}>
                {values.map(v => (
                  <option value={v} key={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ))}
        <div className="mt-2">
          <label>
            ストリング&nbsp;&nbsp;
            <select
              onChange={e => {
                const selectedValue = JSON.parse(e.target.value) // 文字列をオブジェクトに戻す
                handleStringPatternChange(
                  selectedValue.main,
                  selectedValue.cross
                )
              }}
              id="stringPatternSelect"
            >
              <option
                value={JSON.stringify({ main: null, cross: null })}
              ></option>
              {stringPatterns.map(pattern => (
                <option
                  value={JSON.stringify({
                    main: pattern.main,
                    cross: pattern.cross,
                  })}
                  key={`${pattern.main}/${pattern.cross}`}
                >
                  {pattern.main}/{pattern.cross}
                </option>
              ))}
            </select>
          </label>
          {/* 選択されたストリングを表示 */}
          <div className="selected-strings">
            {selectedStrings.map((pattern, index) => (
              <span
                key={`${pattern.main}/${pattern.cross}`}
                className="selected-string"
                onClick={() => removeStringPattern(index)}
              >
                {pattern.main}/{pattern.cross} ✕
              </span>
            ))}
          </div>
        </div>
        <div className="text-center mt-6">
          <button className="search-button" onClick={() => closeFilter()}>
            検索する
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* オーバーレイ (サイドバーが開いたときのみ表示) */}
      {isFilterVisible && (
        <div
          className="filter-overlay"
          onClick={() => setIsFilterVisible(false)} // 背景クリックで閉じる
        ></div>
      )}
      {isFilterVisible && (
        <div className="filter-sp">
          <FilterComponent />
        </div>
      )}
      {/* <div className="racket-wrap"> */}
      <p className="racket-attention">※横スクロールできます</p>
      <p className="racket-attention">
        ※他のブランドも作成中です(少々お待ちください)
      </p>
      <div className="racket-container">
        {/* スマホ用の検索ボタン */}
        <div className="search-button-wrap">
          <div className="racket-num">{sortedRackets.length + `件ヒット`}</div>
          <button className="search-button" onClick={() => openFilter()}>
            <FilterIcon />
            絞り込み
          </button>
        </div>
        <div className="filter-pc">
          <FilterComponent />
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {[
                  { key: "jp", label: "商品名", sortable: true },
                  { key: "price", label: "価格", sortable: false },
                  { key: "brand", label: "ブランド", sortable: true },
                  { key: "is_competition", label: "競技者", sortable: true },
                  { key: "weight", label: "重量", sortable: true },
                  { key: "size", label: "サイズ", sortable: true },
                  { key: "balance", label: "バランス", sortable: true },
                  { key: "beam", label: "フレーム", sortable: true },
                  { key: "length", label: "長さ", sortable: true },
                  {
                    key: "swingweight",
                    label: "スイングウェイト",
                    sortable: true,
                  },
                  { key: "string_main", label: "ストリング", sortable: true },
                  { key: "color", label: "色", sortable: false },
                  { key: "technologies", label: "技術", sortable: false },
                  { key: "series", label: "シリーズ", sortable: true },
                  { key: "year", label: "年", sortable: true },
                  { key: "link", label: "公式", sortable: false },
                ].map(({ key, label, sortable }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key, sortable)}
                    style={{ cursor: sortable ? "pointer" : "default" }}
                  >
                    {label}
                    {sortable && sortConfig.key === key
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRackets.map(({ frontmatter: racket, fields }, index) => (
                <tr key={racket.name + index}>
                  <td className="racket-name">
                    <Link to={fields.slug}>{racket.jp}</Link>
                  </td>
                  {/* <td>{racket.name}</td> */}
                  <td>
                    {racket.amazon ? (
                      <a
                        className="poppotennis-media__btn __orange racket_price_btn"
                        style={{}}
                        href={racket.amazon}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Amazon
                      </a>
                    ) : (
                      ""
                    )}
                    {racket.rakuten ? (
                      <a
                        className="poppotennis-media__btn __red racket_price_btn"
                        href={racket.rakuten}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        楽天市場
                      </a>
                    ) : (
                      ""
                    )}
                    <a
                      className="poppotennis-media__btn __mercari racket_price_btn"
                      href={`https://jp.mercari.com/search?keyword=${racket.brand} ${racket.year} ${racket.name} テニス&order=desc&sort=score&afid=${afid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      メルカリ
                    </a>
                  </td>
                  <td>{racket.brand}</td>
                  <td>{racket.is_competition === 1 ? "⚪︎" : ""}</td>
                  <td>{racket.weight}</td>
                  <td>{racket.size}</td>
                  <td>{racket.balance}</td>
                  <td>
                    {racket.beam
                      ? racket.beam
                      : `${racket.beam_throat}/${racket.beam_mid}/${racket.beam_top}`}
                  </td>
                  <td>{racket.length}</td>
                  <td>{racket.swingweight}</td>
                  <td>{`${racket.string_main}/${racket.string_cross}`}</td>
                  <td>{racket.color}</td>
                  <td className="tooltip-container">
                    {racket.technologies?.length > 0 ? (
                      <span className="tooltip-trigger">
                        {racket.technologies[0]}
                        {racket.technologies.length > 1 && " ..."}
                        <span
                          className={`tooltip-content ${
                            index <= 5 ? "tooltip-bottom" : "tooltip-top"
                          }`}
                        >
                          <ul>
                            {racket.technologies.map((t, j) => (
                              <li key={`tech${index}/${j}`}>{t}</li>
                            ))}
                          </ul>
                        </span>
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{racket.series}</td>
                  <td>{racket.year}</td>
                  <td>
                    {racket.url ? (
                      <a
                        href={racket.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        リンク
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-sm font-bold">
            誤表記や追加して欲しいもののご要望ありましたらお問い合わせからお気軽にご連絡ください。
          </div>
          <a
            href="https://forms.gle/Krop9vVJM94nbAk4A"
            target="_blank"
            rel="noopener noreferrer"
          >
            お問い合わせ
          </a>
        </div>
      </div>
      {/* </div> */}
    </>
  )
}

export default RacketDataComponent
